import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft, GenderMale, GenderFemale } from '@phosphor-icons/react';
import { Slider } from '../components/ui/slider';

export default function OnboardingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const heritage = location.state?.heritage || '';
  const [step, setStep] = useState(0);
  const [gender, setGender] = useState('');
  const [lookingFor, setLookingFor] = useState('');
  const [ageRange, setAgeRange] = useState([20, 40]);

  const handleFinish = () => {
    navigate('/auth', {
      state: { heritage, gender, lookingFor, ageRangeMin: ageRange[0], ageRangeMax: ageRange[1] }
    });
  };

  const GenderButton = ({ value, label, icon: Icon, current, setCurrent }) => (
    <button
      onClick={() => setCurrent(value)}
      data-testid={`onboarding-${value}`}
      className={`flex flex-col items-center justify-center gap-3 p-8 rounded-2xl border transition-all duration-300 w-40 h-40 ${
        current === value
          ? 'bg-[#D4AF37]/10 border-[#D4AF37] shadow-[0_0_20px_rgba(212,175,55,0.2)]'
          : 'bg-white/5 border-white/10 hover:border-[#D4AF37]/30 hover:bg-white/5'
      }`}
    >
      <Icon size={40} weight={current === value ? 'fill' : 'light'} className={current === value ? 'text-[#D4AF37]' : 'text-[#9EABCE]'} />
      <span className={`text-base font-semibold ${current === value ? 'text-[#D4AF37]' : 'text-[#9EABCE]'}`}>{label}</span>
    </button>
  );

  const steps = [
    {
      title: 'I am a...',
      content: (
        <div className="flex gap-6 justify-center">
          <GenderButton value="man" label="Man" icon={GenderMale} current={gender} setCurrent={setGender} />
          <GenderButton value="woman" label="Woman" icon={GenderFemale} current={gender} setCurrent={setGender} />
        </div>
      ),
      valid: !!gender
    },
    {
      title: 'Looking for...',
      content: (
        <div className="flex gap-6 justify-center">
          <GenderButton value="man" label="Man" icon={GenderMale} current={lookingFor} setCurrent={setLookingFor} />
          <GenderButton value="woman" label="Woman" icon={GenderFemale} current={lookingFor} setCurrent={setLookingFor} />
        </div>
      ),
      valid: !!lookingFor
    },
    {
      title: 'Age Range',
      content: (
        <div className="w-full max-w-md mx-auto space-y-8">
          <div className="text-center">
            <span className="text-4xl font-bold text-[#D4AF37] font-['Outfit']">
              {ageRange[0]} - {ageRange[1]}
            </span>
            <p className="text-[#9EABCE] text-sm mt-2">years old</p>
          </div>
          <Slider
            value={ageRange}
            onValueChange={setAgeRange}
            min={18}
            max={65}
            step={1}
            data-testid="age-range-slider"
            className="[&_[role=slider]]:bg-[#D4AF37] [&_[role=slider]]:border-[#D4AF37] [&_[role=slider]]:shadow-[0_0_10px_rgba(212,175,55,0.3)] [&_[data-orientation=horizontal]>.relative]:bg-[#D4AF37]"
          />
          <div className="flex justify-between text-xs text-[#9EABCE]">
            <span>18</span>
            <span>65</span>
          </div>
        </div>
      ),
      valid: true
    }
  ];

  const currentStep = steps[step];

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4" data-testid="onboarding-page">
      <motion.div
        key={step}
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -30 }}
        className="glass-card p-8 md:p-12 max-w-lg w-full text-center"
      >
        {/* Progress Dots */}
        <div className="flex gap-2 justify-center mb-8">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === step ? 'w-8 bg-[#D4AF37]' : i < step ? 'w-4 bg-[#D4AF37]/50' : 'w-4 bg-white/10'
              }`}
            />
          ))}
        </div>

        <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-white font-['Outfit'] mb-8">
          {currentStep.title}
        </h2>

        {currentStep.content}

        <div className="flex gap-4 mt-10 justify-center">
          {step > 0 && (
            <button
              onClick={() => setStep(step - 1)}
              data-testid="onboarding-back-btn"
              className="btn-outline-gold px-6 py-3 flex items-center gap-2 text-sm"
            >
              <ArrowLeft size={18} /> Back
            </button>
          )}
          <button
            onClick={step === steps.length - 1 ? handleFinish : () => setStep(step + 1)}
            disabled={!currentStep.valid}
            data-testid="onboarding-next-btn"
            className={`px-8 py-3 rounded-xl text-sm font-bold flex items-center gap-2 transition-all duration-300 ${
              currentStep.valid ? 'btn-gold' : 'bg-white/5 text-[#9EABCE]/40 cursor-not-allowed'
            }`}
          >
            {step === steps.length - 1 ? 'Get Started' : 'Next'}
            <ArrowRight size={18} />
          </button>
        </div>
      </motion.div>
    </div>
  );
}
